import React, { useState, useEffect } from "react";
import { get } from "../../services/api";

function StudentFeeLedger() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");

  // ---------- State ----------
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [rollNo, setRollNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState("");

  // ---------- Fetch initial options ----------
  useEffect(() => {
    const fetchClasses = async () => {
      if (!centerId || !token) return;
      try {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassesData(response || []);
      } catch (err) {
        console.error("Error fetching classes", err);
      }
    };
    fetchClasses();
  }, [centerId, token]);

  // Update section dropdown when class changes
  useEffect(() => {
    const fetchSections = async () => {
      if (!selectedClassId || !centerId || !token) {
        setSectionsData([]);
        setSelectedSectionId("");
        return;
      }
      try {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${selectedClassId}&centerId=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSectionsData(response || []);
      } catch (err) {
        console.error("Error fetching sections", err);
        setSectionsData([]);
      }
    };
    fetchSections();
  }, [selectedClassId, centerId, token]);

  // ---------- Generate report ----------
  const loadReport = async () => {
    setLoading(true);
    setError("");
    setReportData(null);

    try {
      const classParam = selectedClassId || 0;
      const sectionParam = selectedSectionId || 0;
      const rollNoParam = rollNo || "";

      const response = await get(
        `StudentChallan/GetYearWiseStudentFeeReport?CenterId=${centerId}&ClassId=${classParam}&SectionId=${sectionParam}&RollNo=${rollNoParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response && response.isSuccess) {
        if (response.result) {
          setReportData(response.result);
        } else {
          setError("No record found.");
        }
      } else {
        setReportData(null);
        setError(response?.message || "No data found.");
      }
    } catch (err) {
      setError("Failed to load data. Check API connection.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Helpers ----------
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "—";
    return Number(num).toLocaleString("en-PK", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const exportToCSV = () => {
    if (!reportData) return;
    const { yearlyReports } = reportData;
    const rows = [];
    // Header
    rows.push(["Date", "Month", "Class", "Detail", "Dr Amount", "Cr Amount", "Balance"]);
    // Data rows
    yearlyReports?.forEach((ys) => {
      ys.details?.forEach((t) => {
        rows.push([
          t.date,
          t.month,
          t.className,
          t.detail,
          t.dr || "0",
          t.cr || "0",
          t.balance || "0",
        ]);
      });
    });
    // Student info as first rows?
    const csvContent = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Ledger_${reportData.rollNo}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  // ---------- Styles (matching BillingReport) ----------
  const inputStyle = {
    height: "36px",
    padding: "0 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "13px",
    minWidth: "150px",
    outline: "none",
    color: "#111827",
    backgroundColor: "#ffffff",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6b7280",
    marginBottom: "4px",
    display: "block",
  };

  const btnPrimary = {
    height: "36px",
    padding: "0 18px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#3b82f6",
    color: "#fff",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  const btnSecondary = {
    height: "36px",
    padding: "0 18px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  // ---------- Render ----------
  return (
    <div style={{ paddingBottom: "20px", fontFamily: "Inter, Arial, sans-serif" }}>
      {/* Header */}
      <div style={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e5e7eb",
        padding: "20px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#111827" }}>
            Student Fee Statement
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#6b7280" }}>
            View complete fee ledger for a student
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {reportData && (
            <button onClick={exportToCSV} style={{ ...btnSecondary, display: "flex", alignItems: "center", gap: "6px" }}>
              📥 Export CSV
            </button>
          )}
          <button onClick={() => window.print()} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: "6px" }}>
            🖨 Print
          </button>
        </div>
      </div>

      <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Filter Panel */}
        <div style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "20px 24px",
        }}>
          <p style={{ margin: "0 0 14px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Filters</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
            {/* Class */}
            <div>
              <label style={labelStyle}>Class</label>
              <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} style={inputStyle}>
                <option value="">All Classes</option>
                {classesData.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Section */}
            <div>
              <label style={labelStyle}>Section</label>
              <select
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                disabled={!selectedClassId}
                style={{
                  ...inputStyle,
                  backgroundColor: !selectedClassId ? "#f9fafb" : "#ffffff",
                  color: !selectedClassId ? "#9ca3af" : "#111827",
                }}
              >
                <option value="">All Sections</option>
                {sectionsData.map((sec) => (
                  <option key={sec.id} value={sec.id}>{sec.name}</option>
                ))}
              </select>
            </div>

            {/* Roll No. */}
            <div>
              <label style={labelStyle}>Roll No.</label>
              <input
                type="text"
                value={rollNo}
                onChange={(e) => setRollNo(e.target.value)}
                placeholder="e.g. STU03620"
                style={inputStyle}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              <button onClick={loadReport} style={btnPrimary}>Generate Report</button>
              <button
                onClick={() => {
                  setSelectedClassId("");
                  setSelectedSectionId("");
                  setRollNo("");
                  setReportData(null);
                  setError("");
                }}
                style={btnSecondary}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Loading / Empty / Error / Report */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
            <div style={{ fontSize: "2rem", marginBottom: "12px" }}>⏳</div>
            Loading ledger data…
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#ef4444" }}>
            <div style={{ fontSize: "2rem", marginBottom: "12px" }}>⚠️</div>
            {error}
          </div>
        )}

        {!loading && !error && !reportData && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
            <div style={{ fontSize: "2rem", marginBottom: "12px" }}>📋</div>
            Select filters and click <strong>Generate Report</strong>
          </div>
        )}

        {!loading && !error && reportData && (
          <>
            {/* Student Card */}
            <div style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px 24px",
              display: "flex",
              flexWrap: "wrap",
              gap: "24px",
              alignItems: "center",
            }}>
              <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#111827", flex: 1 }}>
                {reportData.studentName}
              </div>
              <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                <div>
                  <div style={labelStyle}>Roll No.</div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#3b82f6" }}>{reportData.rollNo}</div>
                </div>
                <div>
                  {/* <div style={labelStyle}>A/C #</div> */}
                  {/* <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#3b82f6" }}>{reportData.accountNo || "—"}</div> */}
                </div>
                <div>
                  <div style={labelStyle}>Class</div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#3b82f6" }}>{reportData.className || "—"}</div>
                </div>
                <div>
                  <div style={labelStyle}>Section</div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#3b82f6" }}>{reportData.sectionName || "—"}</div>
                </div>
              </div>
              <div style={{
                background: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderRadius: "8px",
                padding: "8px 16px",
                textAlign: "center",
              }}>
                <div style={{ ...labelStyle, color: "#0369a1" }}>Opening Balance</div>
                <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0369a1" }}>
                  {formatNumber(reportData.openingBalance)}
                </div>
              </div>
            </div>

            {/* Year Sections */}
            {reportData.yearlyReports && reportData.yearlyReports.map((yearSection, idIndex) => {

              return (
                <div key={idIndex} style={{ marginBottom: "24px" }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}>
                    <span style={{ fontFamily: "monospace", fontSize: "1rem", fontWeight: 700, color: "#3b82f6" }}>
                      — {yearSection.year} —
                    </span>
                    <div style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
                  </div>

                  <div style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                            {["Date", "Month", "Class", "Detail", "Debit (Dr)", "Credit (Cr)", "Balance"].map((h, i) => (
                              <th key={i} style={{
                                padding: "11px 14px",
                                textAlign: i < 4 ? "left" : "right",
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                color: "#6b7280",
                              }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {yearSection.details && yearSection.details.map((t, idx) => {
                            const isPayment = t.cr > 0;
                            return (
                              <tr key={idx} style={{
                                borderBottom: "1px solid #f3f4f6",
                                backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                                transition: "background 0.15s",
                              }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#eff6ff")}
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#ffffff" : "#fafafa")
                                }
                              >
                                <td style={{ padding: "10px 14px", fontSize: "13px", color: "#3b82f6" }}>{t.date}</td>
                                <td style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 600, color: "#111827" }}>{t.month}</td>
                                <td style={{ padding: "10px 14px", fontSize: "13px", color: "#6b7280" }}>{t.className}</td>
                                <td style={{ padding: "10px 14px", fontSize: "13px", color: "#374151" }}>{t.detail}</td>
                                
                                <td style={{ padding: "10px 14px", textAlign: "right", color: t.dr ? "#f97316" : "#9ca3af" }}>
                                  {t.dr ? formatNumber(t.dr) : "—"}
                                </td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: t.cr ? "#10b981" : "#9ca3af", fontWeight: t.cr ? 600 : 400 }}>
                                  {t.cr ? formatNumber(t.cr) : "—"}
                                </td>
                                <td style={{
                                  padding: "10px 14px",
                                  textAlign: "right",
                                  fontWeight: 700,
                                  color: t.balance <= 0 ? "#10b981" : "#ef4444",
                                }}>
                                  {formatNumber(t.balance)}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Year summary row */}
                          <tr style={{ backgroundColor: "#f0f9ff", borderTop: "2px solid #e5e7eb" }}>
                            <td colSpan="4" style={{ padding: "12px 14px", fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                              Year {yearSection.year} Summary
                            </td>
                            <td style={{ padding: "12px 14px", textAlign: "right", color: "#f97316", fontWeight: 600 }}>
                              {formatNumber(yearSection.yearlyTotalDr)}
                            </td>
                            <td style={{ padding: "12px 14px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>
                              {formatNumber(yearSection.yearlyTotalCr)}
                            </td>
                            <td style={{
                              padding: "12px 14px",
                              textAlign: "right",
                              fontWeight: 700,
                              color: yearSection.yearlyClosingBalance <= 0 ? "#10b981" : "#ef4444",
                            }}>
                              {formatNumber(yearSection.yearlyClosingBalance)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Grand Totals Card */}
            {(() => {
              const outstanding = reportData.grandSummary?.totalBalance || 0;

              return (
                <div style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "20px 24px",
                  display: "flex",
                  gap: "32px",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={labelStyle}>Opening Balance</div>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#0369a1" }}>{formatNumber(reportData.openingBalance)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={labelStyle}>Total Debit (Dr)</div>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#f97316" }}>{formatNumber(reportData.grandSummary?.totalDr || 0)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={labelStyle}>Total Credit (Cr)</div>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#10b981" }}>{formatNumber(reportData.grandSummary?.totalCr || 0)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={labelStyle}>Net Outstanding</div>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color: outstanding <= 0 ? "#10b981" : "#ef4444" }}>
                      {formatNumber(outstanding)}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Generation timestamp */}
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", textAlign: "right" }}>
              Report generated on {new Date().toLocaleString()}
            </p>
          </>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default StudentFeeLedger;